import { LegalPageViewModel } from "@lumora/core";
import { useViewModel } from "@lumora/ui";
import { useEffect, useMemo } from "react";
import { client, SITE } from "../client.js";

export interface TermsPageProps {
  className?: string;
  viewModel?: LegalPageViewModel;
}

/**
 * Data-driven Service Terms page (#902). Loads structured sections from
 * `site_content.legalPages.terms` via `LegalPageViewModel` — zero hardcoded legal prose in TSX.
 */
export function TermsPage({ className, viewModel }: TermsPageProps) {
  const vm = useMemo(
    () => viewModel ?? new LegalPageViewModel(client, SITE, "terms"),
    [viewModel],
  );
  const state = useViewModel(vm);

  useEffect(() => {
    if (!viewModel) {
      void vm.load();
    }
  }, [vm, viewModel]);

  if (state.loading) {
    return (
      <article className={className} data-lumora-view="terms" data-state="loading">
        <p className="site-utility-kicker">Legal</p>
        <h1>Service terms</h1>
        <p>Loading service terms…</p>
      </article>
    );
  }

  if (state.error) {
    return (
      <article className={className} data-lumora-view="terms" data-state="error">
        <p className="site-utility-kicker">Legal</p>
        <h1>Service terms</h1>
        <p data-lumora-error className="portal-error">{state.error}</p>
        <button type="button" className="portal-btn" onClick={() => void vm.load()}>
          Retry
        </button>
      </article>
    );
  }

  if (!state.document || state.document.isEmpty()) {
    return (
      <article className={className} data-lumora-view="terms" data-state="empty">
        <p className="site-utility-kicker">Legal</p>
        <h1>Service terms</h1>
        <p className="portal-notice" data-lumora-empty>
          Service terms are being updated. Contact Lumora for the current master service agreement.
        </p>
      </article>
    );
  }

  const doc = state.document;

  return (
    <article className={className} data-lumora-view="terms" data-state="ready">
      <p className="site-utility-kicker">{doc.kicker}</p>
      <h1>{doc.title}</h1>
      {doc.lead ? (
        <p className="portal-notice site-utility-lead" data-lumora-lead>
          {doc.lead}
        </p>
      ) : null}
      {doc.sections.map((section) => (
        <section key={section.heading} className="portal-section">
          <h2>{section.heading}</h2>
          {section.lead ? <p data-lumora-lead>{section.lead}</p> : null}
          {section.paragraphs?.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          {section.subsections?.map((block) => (
            <div key={block.heading}>
              <h3>{block.heading}</h3>
              {block.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          ))}
        </section>
      ))}
    </article>
  );
}
